import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAuthStatus = useCallback(async () => {
    try {
      console.log('🔍 Checking auth status...');
      const response = await api.get('/api/auth/me');
      console.log('✅ Auth check successful:', response.data);
      setUser(response.data.user);
    } catch (error) {
      console.error('❌ Auth check failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    console.log('🔍 Initial auth check, token exists:', !!token);
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      checkAuthStatus();
    } else {
      setLoading(false);
    }
  }, [checkAuthStatus]);

  const login = async (username, password) => {
    try {
      console.log('🔍 Attempting login...');
      setError(null);
      const response = await api.post('/api/auth/login', { username, password });
      console.log('✅ Login response:', response.data);
      const { token, user } = response.data;
      
      localStorage.setItem('token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);
      console.log('✅ User set in state:', user);
      return { success: true };
    } catch (error) {
      console.error('❌ Login error:', error);
      const errorData = error.response?.data;
      const errorMessage = errorData?.error || 'Login failed';
      
      // Handle different error types
      if (error.response?.status === 423) {
        // System locked - requires redeploy
        const lockMessage = errorData?.message || 'System locked due to too many failed attempts.';
        const unlockCode = errorData?.unlockCode;
        
        setError(`${lockMessage} Unlock Code: ${unlockCode}`);
        return { 
          success: false, 
          error: errorMessage,
          locked: true,
          systemLocked: true,
          unlockCode,
          message: lockMessage
        };
      } else if (error.response?.status === 401) {
        // Failed login with attempt count
        const attemptsRemaining = errorData?.attemptsRemaining;
        const message = errorData?.message || errorMessage;
        
        setError(message);
        return { 
          success: false, 
          error: errorMessage,
          attemptsRemaining,
          message
        };
      } else {
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setError(null);
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 